import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db70cmhgdmoohm3rngig';

const VALUES = [
  {
    icon: 'ri-compass-3-line',
    title: '清晰易导航',
    desc: '以焦点推荐、分类入口和卡片式列表组织内容，帮助观众第一时间找到目标影片。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '内容说明透明',
    desc: '全站作品均为演示示例，播放仅作界面展示，不接入真实流媒体，避免误导观众。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '影迷视角',
    desc: '围绕观众查阅影视资料的真实需求设计，从简介、演职员到相关推荐一应俱全。',
  },
];

const SCOPE = [
  { label: '影视条目', value: '电影 · 电视剧' },
  { label: '内容类型', value: '科幻 / 悬疑 / 爱情 / 动作 / 古装 等' },
  { label: '覆盖地区', value: '中国大陆 / 中国香港 / 美国 / 日本 / 韩国 / 英国 / 法国' },
  { label: '核心功能', value: '搜索 · 分类筛选 · 排序分页 · 智能推荐' },
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function About() {
  useDocumentMeta(
    '星辰影院-关于我们',
    '了解星辰影院的网站定位、内容范围与服务宗旨。星辰影院收录最新电影与热门电视剧信息，提供便捷的搜索、分类与推荐体验。',
    '关于星辰影院,网站介绍,影视资讯,联系我们,星辰影院',
  );

  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      payload.append(key, value as string);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      formEl.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        {/* hero */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="font-medium text-foreground-800">关于我们</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
              关于星辰影院
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              星辰影院是一个面向影视爱好者的影视浏览与资讯平台。我们聚合最新电影与热门电视剧资源信息，
              提供清晰的分类、便捷的搜索和贴心的推荐，帮助你在海量作品中快速找到想看的内容。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  我们做什么
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700 md:text-base">
                <p>
                  星辰影院专注于影视内容的<strong className="font-semibold text-foreground-900">浏览与检索体验</strong>。
                  站内收录电影与电视剧两大分类，每部作品都配有封面、类型、年份、地区、简介等信息，
                  并按上映时间、播放热度与评分进行整理，方便你从不同维度发现好内容。
                </p>
                <p>
                  我们提供全文关键词搜索、类型 / 年份 / 地区的多维筛选，以及最新、热门、评分多种排序方式。
                  当你在详情页停留时，系统还会根据类型、导演与地区智能推荐相关作品，让发现的过程更自然。
                </p>
                <p>
                  需要特别说明的是：本站所有影视作品、评分与播放数据均为<strong className="font-semibold text-foreground-900">演示示例</strong>，
                  不代表任何真实作品；播放相关功能仅作界面展示，不接入真实流媒体内容，也不提供下载或付费服务。
                </p>
              </div>

              <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {VALUES.map((item) => (
                  <div key={item.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-2xl`} />
                    </span>
                    <h3 className="mt-4 text-base font-semibold text-foreground-900">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <div className="flex items-center gap-2.5">
                  <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    内容范围
                  </h2>
                </div>
                <dl className="mt-4 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:grid-cols-2">
                  {SCOPE.map((row) => (
                    <div key={row.label} className="flex gap-4 bg-background-50 px-4 py-3.5">
                      <dt className="w-20 shrink-0 text-sm text-foreground-500">{row.label}</dt>
                      <dd className="text-sm font-medium text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* contact + form */}
            <aside className="space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-bold text-foreground-900">联系我们</h3>
                <ul className="mt-4 space-y-3.5">
                  <li>
                    <a
                      href="mailto:support@starcinema.example.com"
                      className="flex items-center gap-3 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg" />
                      </span>
                      support@starcinema.example.com
                    </a>
                  </li>
                  <li>
                    <a
                      href="mailto:media@starcinema.example.com"
                      className="flex items-center gap-3 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                        <i className="ri-briefcase-line text-lg" />
                      </span>
                      media@starcinema.example.com
                    </a>
                  </li>
                  <li>
                    <div className="flex items-center gap-3 text-sm text-foreground-700">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                        <i className="ri-customer-service-2-line text-lg" />
                      </span>
                      周一至周五 10:00 - 18:00
                    </div>
                  </li>
                </ul>
                <div className="mt-5 flex items-center gap-2.5 border-t border-background-200 pt-4">
                  {['ri-weibo-line', 'ri-wechat-line', 'ri-bilibili-line', 'ri-bookmark-3-line'].map((icon) => (
                    <span
                      key={icon}
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-primary-600 hover:text-background-50"
                    >
                      <i className={`${icon} text-lg`} />
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-bold text-foreground-900">反馈与建议</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                  遇到问题或有内容建议？填写下方表单，我们会尽快处理。
                </p>

                <form
                  id="about-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-4 space-y-3.5"
                >
                  <div>
                    <label htmlFor="about-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      你的称呼
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入称呼"
                      className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      电子邮箱
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-topic" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      反馈类型
                    </label>
                    <select
                      id="about-topic"
                      name="topic"
                      defaultValue="内容反馈"
                      className="w-full cursor-pointer rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                    >
                      <option value="内容反馈">内容反馈</option>
                      <option value="功能建议">功能建议</option>
                      <option value="问题反馈">问题反馈</option>
                      <option value="合作洽谈">合作洽谈</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="about-message" className="mb-1.5 block text-xs font-medium text-foreground-700">
                      具体内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="请描述你的问题或建议（不超过 500 字）"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  {/* anti-spam honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    className="contact-extra-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
                    {status === 'submitting' ? '提交中…' : '提交反馈'}
                  </button>

                  {status === 'success' && (
                    <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3.5 py-2.5 text-xs font-medium text-accent-900">
                      <i className="ri-checkbox-circle-line text-base" />
                      提交成功，感谢你的反馈，我们会尽快处理。
                    </p>
                  )}
                  {status === 'error' && (
                    <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3.5 py-2.5 text-xs font-medium text-secondary-900">
                      <i className="ri-error-warning-line text-base" />
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}